import { useApolloClient } from '@apollo/client';
import { ImgElement } from '@axinom/mosaic-ui';
import clsx from 'clsx';
import * as lscache from 'lscache';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ensureError } from '../../../common/errors/ensure-error';
import { useAuthenticateTenantAdminMutation } from '../../../generated/id-service-env-administration';
import arrow from '../../../images/ArrowRight.svg';
import logo from '../../../images/AxinomLogo.png';
import type { Tenant } from '../../../models/tenant';
import { authStateStore } from '../../../state-store';
import { extractErrorString } from '../../../util/helper';
import { ChangePassword } from '../ChangePassword/ChangePassword';
import classes from './SignIn.module.scss';

export const SignIn: React.FC = () => {
  const apolloClient = useApolloClient();
  const TENANTS_STORE_KEY = 'tenantsStore';

  const history = useHistory();
  const { tId } = useParams<{ tId: string }>();

  const [showChangePassword, setShowChangePassword] = useState<boolean>(false);
  const [error, setError] = React.useState('');
  const [tenantId, setTenantId] = React.useState<string>(
    tId && tId.length > 20 ? tId : '',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const [storedTenants, setStoredTenants] = useState<Tenant[]>([]);

  useEffect(() => {
    const storedTenants = JSON.parse(
      lscache.get(TENANTS_STORE_KEY) || '[]',
    ) as Tenant[];
    setStoredTenants(storedTenants);
  }, []);

  const saveTenant = (newTenant: Tenant): void => {
    const tEntryIdx = storedTenants.findIndex(
      (t) => t.tenantId === newTenant.tenantId,
    );
    if (tEntryIdx === -1) {
      const newTenants = [...storedTenants, newTenant];
      lscache.set(TENANTS_STORE_KEY, JSON.stringify(newTenants));
      setStoredTenants(newTenants);
    }
  };

  const [loginUser] = useAuthenticateTenantAdminMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const handleSignIn = async (): Promise<void> => {
    setError('');

    setBusy(true);
    lscache.set('currentTenantId', tenantId);
    try {
      if (tenantId === '' || email === '' || password === '') {
        throw new Error('Tenant ID, email and password are required.');
      }
      const res = await loginUser({
        variables: {
          input: {
            email,
            password,
            tenantId,
          },
        },
      });
      setBusy(false);

      const authResponse = res.data?.authenticateTenantAdmin;
      saveTenant({
        tenantId: tenantId,
        name: authResponse?.tenantName as string,
      });

      const validity = authResponse?.expiresInSeconds as number;

      lscache.set('currentTenantId', tenantId);
      lscache.set('currentTenantName', authResponse?.tenantName);
      lscache.set('tenantAdminName', authResponse?.tenantAdminName);
      lscache.set('tenantAdminEmail', authResponse?.tenantAdminEmail);
      lscache.set('requirePasswordChange', !authResponse?.passwordChanged);
      lscache.set('accessToken', authResponse?.accessToken, validity);

      authStateStore.setState({
        tenantAdminName: authResponse?.tenantAdminName,
        tenantAdminEmail: authResponse?.tenantAdminEmail,
        tenantName: authResponse?.tenantName,
        tenantId: tenantId,
        accessToken: authResponse?.accessToken,
      });

      if (authResponse?.passwordChanged) {
        history.push('/');
      } else {
        setShowChangePassword(true);
      }
    } catch (e) {
      const error = ensureError(e);
      setBusy(false);
      setError(extractErrorString(error));
    }
  };

  const handleClearSuggestions = (): void => {
    localStorage.removeItem(TENANTS_STORE_KEY);
    setStoredTenants([]);
    setShowSuggestions(false);
  };

  const handleTenantSelection = (tenantId: string): void => {
    setShowSuggestions(false);
    setTenantId(tenantId);
  };

  const canSignIn = (): boolean => {
    return password === '' || email === '' || tenantId === '';
  };

  const [showSuggestions, setShowSuggestions] = useState(false);

  return showChangePassword ? (
    <ChangePassword isFirstTime={true} currentPwd={password} />
  ) : (
    <>
      <form
        className={classes.container}
        onSubmit={() => {
          handleSignIn();
        }}
      >
        <div className={classes.wrapper}>
          <div className={classes.box}>
            <div className={classes.logo}>
              <ImgElement src={logo} alt="Axinom Logo" />
            </div>
            <div className={classes.login}>Sign In</div>
            <div className={classes.subText}>Please sign in to proceed</div>
            <span data-test-id="error-msg">{error}</span>
            <input
              type="text"
              placeholder="Tenant ID"
              value={tenantId}
              onChange={(e) => {
                setTenantId(e.target.value);
                setShowSuggestions(false);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() =>
                setTimeout(() => {
                  setShowSuggestions(false);
                }, 300)
              }
              data-test-id="tenant-id-field"
            />
            {showSuggestions && (
              <div className={classes.suggestions}>
                {storedTenants.map((tenant, idx) => (
                  <div key={tenant.tenantId} className={classes.tenant}>
                    <div
                      className={classes.name}
                      onClick={() => handleTenantSelection(tenant.tenantId)}
                    >
                      {tenant.name}
                    </div>
                    <div
                      className={classes.id}
                      onClick={() => handleTenantSelection(tenant.tenantId)}
                    >
                      {tenant.tenantId}
                    </div>
                    {idx === storedTenants.length - 1 && (
                      <div
                        className={classes.clear}
                        onClick={handleClearSuggestions}
                      >
                        clear
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <input
              type="text"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-test-id="email-field"
            />
            <input
              type="password"
              placeholder="Password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSignIn();
                }
              }}
              data-test-id="password-field"
            />
            <div>
              <div
                className={clsx(classes.submit, {
                  [classes.disabled]: busy || canSignIn(),
                })}
                onClick={handleSignIn}
                data-test-id="sign-in-button"
              >
                <div className={classes.text}>Sign In</div>
                <div className={classes.icon}>
                  <ImgElement src={arrow} decorative={true} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};
